import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Mails, Loader2, Check, Send, RotateCcw, Eye, Languages } from "lucide-react";
import api from "../../api";
import { RichText } from "./RichText";
import { CollapsibleList } from "./CollapsibleList";
import { useToast } from "../../hooks/use-toast";

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
];

export const AdminEmailTemplates = () => {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [key, setKey] = useState("");
  const [lang, setLang] = useState("en");
  const [tpl, setTpl] = useState(null);
  const [form, setForm] = useState({ subject: "", title: "", body: "" });
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/email-templates").then((r) => setList(r.data.templates || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (k, l = lang) => {
    setKey(k); setLang(l); setTpl(null); setPreview(null);
    try {
      const { data } = await api.get(`/admin/email-templates/${k}`, { params: { lang: l } });
      setTpl(data);
      setForm({ subject: data.subject, title: data.title, body: data.body });
    } catch { toast({ title: "Could not open that email" }); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/admin/email-templates/${key}`, form, { params: { lang } });
      setTpl(data);
      await load();
      toast({ title: "Wording saved", description: lang === "en"
        ? "It's live on the next email we send."
        : `Live for everyone reading LytePress in ${LANGS.find((l) => l.code === lang)?.label}.` });
    } catch (e) { toast({ title: e.response?.data?.detail || "Could not save" }); }
    finally { setBusy(""); }
  };

  const autoTranslate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/admin/email-templates/${key}/translate`, null, { params: { lang } });
      setForm({ subject: data.subject, title: data.title, body: data.body });
      toast({ title: "Draft translated", description: "Read it over, tweak anything, then Save." });
    } catch { toast({ title: "Could not translate that one" }); }
    finally { setBusy(""); }
  };

  const doPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post(`/admin/email-templates/${key}/preview`, form);
      setPreview(data);
    } catch { toast({ title: "Could not build a preview" }); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/email-templates/${key}/test`, null, { params: { lang } });
      toast({ title: data.sent ? `Test sent to ${data.to}` : "Email isn't wired up here",
              description: data.subject });
    } catch { toast({ title: "Could not send the test" }); }
    finally { setBusy(""); }
  };

  const revert = async () => {
    if (!window.confirm(lang === "en"
      ? "Put this email back to the wording we shipped?"
      : "Delete this translation? The email falls back to English.")) return;
    setBusy("reset");
    try {
      const { data } = await api.post(`/admin/email-templates/${key}/reset`, null, { params: { lang } });
      setTpl(data);
      setForm({ subject: data.subject, title: data.title, body: data.body });
      await load();
      toast({ title: lang === "en" ? "Back to the original wording" : "Translation removed" });
    } catch { toast({ title: "Could not revert" }); }
    finally { setBusy(""); }
  };

  const btn = "text-xs font-bold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-email-templates">
      <p className="text-base font-extrabold text-foreground flex items-center gap-2"><Mails size={18} className="text-[#16a34a]" /> Email wording</p>
      <p className="text-sm text-muted-foreground mt-1">
        The twelve emails that carry real copy. Password resets, receipts and security notices stay in code on purpose.
      </p>

      <CollapsibleList collapsedCount={4} className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" testid="email-tpl-list">
        {list.map((row) => (
          <button key={row.key} onClick={() => open(row.key)} data-testid={`email-tpl-${row.key}`}
            className={`text-left border rounded-xl px-3 py-2.5 transition-colors ${key === row.key ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]" : "border-border hover:border-[#16a34a]/50"}`}>
            <p className="text-sm font-bold text-foreground truncate">{row.label}</p>
            <p className="text-xs text-muted-foreground truncate">{row.subject}</p>
            {row.edited && <span className="text-[10px] font-extrabold text-[#16a34a]">EDITED</span>}
          </button>
        ))}
      </CollapsibleList>

      {key && !tpl && <div className="mt-4 h-24 rounded-xl bg-secondary animate-pulse" />}

      {tpl && (
        <div className="mt-5 pt-5 border-t border-border space-y-3" data-testid="email-tpl-editor">
          <p className="text-sm font-extrabold text-foreground">{tpl.label}</p>
          {(tpl.translated || []).length > 0 && (
            <p className="text-xs text-muted-foreground -mt-2">Translated: {(tpl.translated || []).map((l) => LANGS.find((x) => x.code === l)?.label || l).join(", ")}</p>
          )}
          <div className="flex flex-wrap gap-1.5" data-testid="email-tpl-langs">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => open(key, l.code)} data-testid={`email-tpl-lang-${l.code}`}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${lang === l.code
                  ? "bg-[#16a34a] border-[#16a34a] text-white"
                  : "border-border text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
                {l.label}{l.code !== "en" && (tpl.translated || []).includes(l.code) ? " ✓" : ""}
              </button>
            ))}
          </div>
          {lang !== "en" && (
            <div className="rounded-xl bg-secondary p-3" data-testid="email-tpl-english-ref">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">English original</p>
              <p className="text-xs font-bold text-foreground mt-1">{tpl.english?.subject}</p>
              <div className="text-xs text-muted-foreground mt-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tpl.english?.body || "") }} />
              <button onClick={autoTranslate} disabled={!!busy} data-testid="email-tpl-translate"
                className="mt-2 text-xs font-bold text-[#16a34a] hover:underline inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy === "translate" ? <Loader2 className="animate-spin" size={13} /> : <Languages size={13} />}
                Draft this translation for me
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5" data-testid="email-tpl-vars">
            {(tpl.vars || []).map((v) => (
              <code key={v} className="text-[11px] font-bold bg-secondary rounded-lg px-2 py-1 text-foreground">{`{${v}}`}</code>
            ))}
            <span className="text-[11px] text-muted-foreground self-center">paste these anywhere in the subject, heading or body</span>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground block mb-1">Subject line</label>
            <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              data-testid="email-tpl-subject"
              className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-2 ring-[#16a34a]/40" />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground block mb-1">Heading inside the email</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="email-tpl-title"
              className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-2 ring-[#16a34a]/40" />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground block mb-1">Body</label>
            <RichText value={form.body} onChange={(html) => setForm((f) => ({ ...f, body: html }))}
              testid="email-tpl-body" minHeight={180} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={save} disabled={!!busy} data-testid="email-tpl-save"
              className="text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 inline-flex items-center gap-1.5">
              {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Save — goes live
            </button>
            <button onClick={doPreview} disabled={!!busy} className={btn} data-testid="email-tpl-preview">
              {busy === "preview" ? <Loader2 className="animate-spin" size={13} /> : <Eye size={13} />} Preview
            </button>
            <button onClick={sendTest} disabled={!!busy} className={btn} data-testid="email-tpl-test">
              {busy === "test" ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send test to me
            </button>
            {(lang === "en" ? tpl.edited : (tpl.translated || []).includes(lang)) && (
              <button onClick={revert} disabled={!!busy} className={btn} data-testid="email-tpl-reset">
                {busy === "reset" ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={13} />} {lang === "en" ? "Revert to original" : "Delete translation"}
              </button>
            )}
          </div>
          {tpl.updatedAt && (
            <p className="text-xs text-muted-foreground" data-testid="email-tpl-updated">
              Last edited {new Date(tpl.updatedAt).toLocaleString()}{tpl.updatedBy ? ` by ${tpl.updatedBy}` : ""}
            </p>
          )}

          {preview && (
            <div data-testid="email-tpl-preview-pane">
              <p className="text-xs font-bold text-muted-foreground mt-2">Subject: <span className="text-foreground">{preview.subject}</span></p>
              <iframe title="Email preview" srcDoc={preview.html} className="mt-2 w-full h-80 rounded-xl border border-border bg-white" />
            </div>
          )}

          {(tpl.versions || []).length > 0 && (
            <details className="mt-2" data-testid="email-tpl-history">
              <summary className="text-xs font-bold text-muted-foreground cursor-pointer">Earlier wording ({tpl.versions.length})</summary>
              <div className="mt-2 space-y-2">
                {[...tpl.versions].reverse().map((v, i) => (
                  <div key={i} className="border border-border rounded-xl p-3">
                    <p className="text-[11px] text-muted-foreground">{v.at ? new Date(v.at).toLocaleString() : ""}{v.by ? ` · ${v.by}` : ""}</p>
                    <p className="text-xs font-bold text-foreground mt-1">{v.subject}</p>
                    <button onClick={() => setForm({ subject: v.subject, title: v.title, body: v.body })}
                      data-testid={`email-tpl-restore-${i}`}
                      className="mt-1.5 text-[11px] font-bold text-[#16a34a] hover:underline">Load this wording</button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminEmailTemplates;
